import Box from "@mui/material/Box";
import { React, useState, useEffect } from "react";
import {
  Avatar, Button, Divider, Typography, ToggleButton, ToggleButtonGroup,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useGetUserInfoQuery, useGetUsersRolesQuery, usePutUserValueMutation } from "../../services/ClientService";
import useFetchWithMsal from "../../utils/useFetchWithMsal";
import { LoadingPanel } from "../../shared/components/LoadingPanel";
import UploadFileIcon from '@mui/icons-material/UploadFile';
import GestureIcon from '@mui/icons-material/Gesture';
import AutocompleteWithCheckboxes from '../../shared/components/AutocompleteWithCheckboxes';
import { SignaturePad } from './SignaturePad';

export const SignatureDowloadTab = ({ open, onClose }) => {
  const { skip } = useFetchWithMsal();


  const [selectedRoles, setSelectedRoles] = useState([]);
  const [rolesInitialized, setRolesInitialized] = useState(false);
  const [signatureImage, setSignatureImage] = useState();
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState();
  // 'upload' = pick/drop an image file (existing); 'draw' = hand-draw on a canvas.
  const [mode, setMode] = useState('upload');

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const { data: userRoles, isLoading: isRolesLoading } = useGetUsersRolesQuery(undefined, { skip });
  const [putUserValue, { isLoading: isSaving }] = usePutUserValueMutation();
  const { data: basicInfo, isLoading: isInfoLoading } = useGetUserInfoQuery(undefined, { skip });
  // Wait for both queries AND the default-role init before rendering the form — otherwise the
  // role select / Save button render one frame empty and flicker to their populated state.
  const isLoading = isRolesLoading || isInfoLoading || !rolesInitialized;

  const currentUserRoleId = Number(basicInfo?.roleId ?? basicInfo?.RoleId ?? 0);
  const isUserManagementAdmin = Number(
    basicInfo?.userManagementRoleId ?? basicInfo?.UserManagementRoleId ?? 0
  ) === 1;

  const fullName = [basicInfo?.firstName, basicInfo?.lastName].filter(Boolean).join(' ') || '';
  const initials = [basicInfo?.firstName?.[0], basicInfo?.lastName?.[0]].filter(Boolean).join('').toUpperCase() || '?';
  const email = basicInfo?.email ?? basicInfo?.Email ?? '';

  // Existing saved signature (GET /api/Users/Me returns it as base64) and the just-picked file.
  const savedSignature = basicInfo?.signature ?? basicInfo?.Signature ?? null;
  const savedSignatureSrc = savedSignature?.base64Data
    ? `data:${savedSignature.contentType || 'image/png'};base64,${savedSignature.base64Data}`
    : null;
  const newSignatureSrc = signatureImage?.base64Data
    ? `data:${signatureImage.contentType || 'image/png'};base64,${signatureImage.base64Data}`
    : null;
  const previewSrc = newSignatureSrc || savedSignatureSrc;
  const currentRoleNames = Array.isArray(userRoles) && currentUserRoleId
    ? (userRoles.find((r) => Number(r.id) === currentUserRoleId)?.name ?? '')
    : '';

  useEffect(() => {
    if (!Array.isArray(userRoles)) return; // roles still loading

    if (userRoles.length > 0) {
      const matched = currentUserRoleId
        ? userRoles.find((r) => Number(r?.id) === currentUserRoleId)
        : null;
      setSelectedRoles(matched ? [matched] : [userRoles[0]]);
    }
    // Gate the form until the default role is applied, so the select/Save button don't
    // render one frame empty (and then flicker to the populated state).
    setRolesInitialized(true);
  }, [userRoles, currentUserRoleId]);

  const handleRolesChange = (_name, newValue) => {
    setSelectedRoles(newValue || []);
  };

  const handleModeChange = (_e, newMode) => {
    if (!newMode || newMode === mode) return;
    // Switching input method drops the in-progress (unsaved) pick so the two modes don't collide.
    setMode(newMode);
    setSignatureImage(undefined);
    setFileName(undefined);
  };

  // Canvas pad emits { base64Data, contentType } (PNG) — or null on clear.
  const handleDraw = (sig) => {
    if (!sig) {
      setSignatureImage(undefined);
      return;
    }
    setSignatureImage({ id: 1, base64Data: sig.base64Data, contentType: sig.contentType });
  };

  const handleImageUpload = (data) => {
    const file = data[0];
    setFileName(file?.name)
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target.result;
        setSignatureImage({
          id: 1,
          base64Data: base64String.split(",")[1],
          contentType: file.type,
        });
      };

      reader.readAsDataURL(file);
    }
  };

  const saveSignature = async () => {
    try {
      for (const roleObj of selectedRoles) {
        await putUserValue({ roleId: roleObj.id, signature: signatureImage }).unwrap();
      }
      setSnackbar({ open: true, message: 'Signature saved successfully', severity: 'success' });
      setTimeout(() => { window.location.reload(); }, 800);
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to save signature', severity: 'error' });
    }
  };

  return (
      <>
        <Dialog open={!!open} onClose={onClose} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ pr: 6 }}>
            Profile
            <IconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
          {isLoading && <LoadingPanel />}
          {!isLoading && (
          <Box>

            {/* ── Profile info ── */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, backgroundColor: '#fff' }}>
              <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main', fontSize: 22, fontWeight: 600 }}>
                {initials}
              </Avatar>
              <Box>
                {fullName && <Typography variant="h2" sx={{ mb: 0.25 }}>{fullName}</Typography>}
                {email && <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>{email}</Typography>}
                {currentRoleNames && <Typography variant="body2" color="text.secondary">Role: {currentRoleNames}</Typography>}
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ mb: 3 }}>
              <AutocompleteWithCheckboxes
                multiple
                name="roles"
                label="Role(s)"
                value={selectedRoles}
                data={userRoles || []}
                setValue={handleRolesChange}
                width="100%"
                disabled={!isUserManagementAdmin}
              />
            </Box>

            {/* Choose how to provide the signature: upload an image or draw it. */}
            <ToggleButtonGroup
              value={mode}
              exclusive
              size="small"
              onChange={handleModeChange}
              sx={{ mb: 2 }}
            >
              <ToggleButton value="upload">
                <UploadFileIcon sx={{ fontSize: 16, mr: 0.75 }} /> Upload image
              </ToggleButton>
              <ToggleButton value="draw">
                <GestureIcon sx={{ fontSize: 18, mr: 0.75 }} /> Draw
              </ToggleButton>
            </ToggleButtonGroup>

            {mode === 'upload' ? (
              /* Drag-and-drop zone — matches the client document upload */
              <Box
                component="label"
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  const file = e.dataTransfer.files?.[0];
                  if (file) handleImageUpload([file]);
                }}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  border: '2px dashed',
                  borderColor: dragOver ? 'primary.main' : 'grey.400',
                  borderRadius: 2,
                  p: 3,
                  cursor: 'pointer',
                  backgroundColor: dragOver ? 'action.hover' : 'grey.50',
                  transition: 'border-color 0.2s, background-color 0.2s',
                  '&:hover': { borderColor: 'primary.main', backgroundColor: 'action.hover' },
                }}
              >
                {previewSrc ? (
                  <>
                    <Box
                      component="img"
                      src={previewSrc}
                      alt="Signature"
                      sx={{ maxHeight: 120, maxWidth: '100%', objectFit: 'contain', backgroundColor: '#fff', p: 0.5, borderRadius: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {newSignatureSrc ? (fileName || 'New signature selected') : 'Current signature'} — drag &amp; drop or click to replace
                    </Typography>
                  </>
                ) : (
                  <>
                    <UploadFileIcon sx={{ fontSize: 36, color: dragOver ? 'primary.main' : 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Drag &amp; drop a signature image here, or click to browse
                    </Typography>
                  </>
                )}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => { if (e.target.files?.[0]) handleImageUpload(e.target.files); }}
                />
              </Box>
            ) : (
              <Box>
                {/* Show the current saved signature as a reference while drawing a new one. */}
                {savedSignatureSrc && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Current:</Typography>
                    <Box
                      component="img"
                      src={savedSignatureSrc}
                      alt="Current signature"
                      sx={{ maxHeight: 48, maxWidth: 200, objectFit: 'contain', backgroundColor: '#fff', p: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                    />
                  </Box>
                )}
                <SignaturePad onChange={handleDraw} />
              </Box>
            )}
          </Box>
          )}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} variant="outlined" color="primary">Cancel</Button>
            <Button onClick={saveSignature} variant="contained" disabled={isLoading || isSaving || selectedRoles.length === 0}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={2500}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </>
  );
};
